import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Check, Send } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { firstLookService } from "../api/services/firstLook.service";
import { SELL_STEPS } from "../constants/ProcessStep.constants.ts";
import { Alert, Button, Card, PageContainer, PageHeader, cn, toast } from "../components/ui";
import type { FullPropertyForm } from "../interface/PropertyForm.ts";
import listingsService, { CreateListingDto } from "../api/services/listings.service.ts";
import { PostUnderInviteSelect } from "../components/Listings/PostUnderInviteSelect";
import { userService } from "../api/services/user.service";
import { localDateTimeToIso } from "../components/property-listing/sellerListingUtils";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { updateProfile } from "../store/authSlice";
import { useTier } from "../hooks/useTier";
import { TierBenefitsModal } from "../components/TierBenefits";

export type AddPropertyOutletContext<TData = Partial<FullPropertyForm>> = {
    formData: Partial<FullPropertyForm>;
    updateFormData: (data: TData) => void;
    submitForm: (data: TData) => void;
    setFormId: (id: string) => void;
    isSubmitting: boolean;
    uploadProgress: string;
    setIsFormValid: (isValid: boolean) => void;
    isFormValid: boolean;
    setIsPropertyLoaded: (loaded: boolean) => void;
    isPropertyLoaded: boolean;
};

/** Short, seller-facing names and summaries for each wizard step */
const STEP_COPY: Record<number, { title: string; description: string }> = {
    1: { title: "Property", description: "Address, price & facts" },
    2: { title: "Condition", description: "Disclosures & documents" },
    3: { title: "Access & photos", description: "Buyers, deadline & photos" },
};

const visibleSteps = SELL_STEPS.filter((s) => !s.hidden);

interface StepperProps {
    currentIndex: number;
    allComplete?: boolean;
}

const Stepper = ({ currentIndex, allComplete }: StepperProps) => (
    <nav aria-label="Listing progress" className="pb-5">
        <ol className="flex items-center gap-2 sm:gap-3">
            {visibleSteps.map((step, index) => {
                const isComplete = allComplete || index < currentIndex;
                const isActive = !allComplete && index === currentIndex;
                const copy = STEP_COPY[step.number] ?? { title: step.label, description: "" };
                const isLast = index === visibleSteps.length - 1;

                return (
                    <li
                        key={step.number}
                        className={cn("flex min-w-0 items-center gap-2 sm:gap-3", !isLast && "flex-1")}
                        aria-current={isActive ? "step" : undefined}
                    >
                        <span
                            className={cn(
                                "tabular flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                                isComplete && "bg-brand-600 text-white",
                                isActive && "bg-gray-900 text-white",
                                !isComplete && !isActive && "bg-white text-gray-500 ring-1 ring-inset ring-gray-300"
                            )}
                        >
                            {isComplete ? <Check className="size-3.5" strokeWidth={3} /> : step.number}
                            <span className="sr-only">{isComplete ? " (completed)" : isActive ? " (current)" : ""}</span>
                        </span>
                        <span className={cn("min-w-0", !isActive && "hidden md:block")}>
                            <span
                                className={cn(
                                    "block truncate text-sm font-medium",
                                    isActive || isComplete ? "text-gray-900" : "text-gray-500"
                                )}
                            >
                                {copy.title}
                            </span>
                            <span className="hidden truncate text-xs text-gray-500 lg:block">{copy.description}</span>
                        </span>
                        {!isLast && (
                            <span
                                aria-hidden
                                className={cn("h-px min-w-4 flex-1", isComplete ? "bg-brand-600" : "bg-gray-300")}
                            />
                        )}
                    </li>
                );
            })}
        </ol>
    </nav>
);

/**
 * Buyer-only accounts reach /sell/add from links and bookmarks. Listing a property makes them a
 * seller as well, so say that and let them opt in (role → "both") before showing the wizard.
 */
const BecomeSellerCard = ({ isUnverified }: { isUnverified: boolean }) => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const enableSelling = useMutation({
        mutationFn: () => userService.updateProfile({ role: "both" }),
        onSuccess: (profile) => {
            dispatch(updateProfile({ role: profile.role ?? "both" }));
            queryClient.setQueryData(["userProfile"], profile);
        },
        onError: () => {
            toast.error("We couldn't turn on selling", { description: "Please try again in a moment." });
        },
    });

    return (
        <Card className="grid gap-6 sm:grid-cols-[1fr_auto] sm:items-center">
            <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900">List a property as a seller</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Your account is set up for buying. Turn on selling to list properties and receive bids from
                    cash buyers. You keep everything you use as a buyer.
                </p>
                {isUnverified && (
                    <p className="mt-2 text-meta text-gray-500">
                        Buyers see your verification level on every listing.{" "}
                        <Link
                            to="/profile/verification"
                            className="font-medium text-gray-900 underline underline-offset-2 hover:text-brand-700"
                        >
                            Get verified
                        </Link>
                    </p>
                )}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button variant="secondary" onClick={() => navigate("/buy")} disabled={enableSelling.isPending}>
                    Not now
                </Button>
                <Button variant="brand" loading={enableSelling.isPending} onClick={() => enableSelling.mutate()}>
                    Turn on selling
                </Button>
            </div>
        </Card>
    );
};

const AddPropertyLayout = () => {
    const role = useAppSelector((s) => s.auth.role);
    const isBuyerOnly = role === "buyer";
    const { tier, isLoading: isTierLoading } = useTier();
    const [isTierModalOpen, setIsTierModalOpen] = useState(false);
    const [formData, setFormData] = useState<Partial<FullPropertyForm>>({});
    const [formId, setFormId] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<string>("");
    const [isFormValid, setIsFormValid] = useState(false);
    const [isPropertyLoaded, setIsPropertyLoaded] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // FR-08: which agent's seller invite this listing is posted under (null = not picked yet)
    const [referralInviteId, setReferralInviteId] = useState<string | null>(null);
    const location = useLocation();
    const navigate = useNavigate();

    // "List another property" from the success page must start a clean wizard;
    // this layout stays mounted across steps, so reset its state before children render.
    const [seenLocationKey, setSeenLocationKey] = useState(location.key);
    if (location.key !== seenLocationKey) {
        setSeenLocationKey(location.key);
        if ((location.state as { resetWizard?: boolean } | null)?.resetWizard) {
            setFormData({});
            setFormId("");
            setIsFormValid(false);
            setIsPropertyLoaded(false);
            setSubmitError(null);
            setReferralInviteId(null);
        }
    }

    const isSuccessPage = location.pathname === '/sell/add/success';
    // MKT-43: sellers invited through a Command company are told up front who sees the deal first
    const { data: firstLook } = useQuery({
        queryKey: ["first-look", "eligibility", formData.state],
        queryFn: () => firstLookService.getEligibility(formData.state || undefined),
        enabled: !isBuyerOnly,
        staleTime: 5 * 60_000,
    });
    const createdListing = (location.state as { listing?: { status?: string; firstLookCompanyName?: string | null } } | null)
        ?.listing;
    const publishedToFirstLook = isSuccessPage && createdListing?.status === "First Look";
    const waitlisted = isSuccessPage && createdListing?.status === "Waitlist";
    // The footer is always there on the form steps. On step 1 it runs the public-records lookup
    // first when it hasn't run yet, so manual entry is never a dead end.
    const showActionBtns = !isSuccessPage && !isBuyerOnly;
    const updateFormData = (newData: Partial<FullPropertyForm>) => {
        setFormData((prevData) => ({...prevData, ...newData}));
    };

    const submitForm = async (finalStepData: Partial<FullPropertyForm>) => {
        const finalData = {...formData, ...finalStepData};

        try {
            setIsSubmitting(true);
            setSubmitError(null);
            setUploadProgress("Creating listing…");

            // Transform form data to match API CreateListingDto
            const listingData: CreateListingDto = {
                title: `${finalData.propertyType || 'Property'} at ${finalData.address || 'Address'}`,
                description: finalData.propertyDescription || '',
                street: finalData.address || '',
                city: finalData.city || '',
                state: finalData.state || '',
                zip: finalData.zipCode || '',
                county: finalData.county || undefined,
                apn: finalData.parcelNumber || undefined,
                propertyType: finalData.propertyType || 'SINGLE_FAMILY',
                sqft: Number(finalData.totalLivingArea) || 0,
                bedsCount: Number(finalData.totalBedrooms) || 0,
                bathsFull: Math.floor(Number(finalData.totalBaths) || 0),
                bathsHalf: (Number(finalData.totalBaths) || 0) % 1 > 0 ? 1 : 0,
                lotSize: finalData.lotSize ? Number(finalData.lotSize) : undefined,
                yearBuilt: finalData.yearBuilt ? Number(finalData.yearBuilt) : undefined,
                askingPrice: Number(finalData.askingPrice) || 0,
                rehabDetails: undefined, // Not used currently
                unpermittedAdditionsDescription: finalData.unpermittedAdditions || undefined,
                // Master §6: only Verified buyers see full addresses; sellers can't override it.
                allowTier1Access: false,
                // datetime-local is the seller's local time; send an unambiguous ISO timestamp
                bidDeadline: localDateTimeToIso(finalData.bidDeadline),
                notifyNetwork: finalData.notifyNetwork ?? false, // Story 6.7
                taggedUserIds: finalData.taggedUserIds || [], // Story 6.6
                // Additional deal details
                sellerType: finalData.sellerType || undefined,
                // FR-39: only an agent chooses; everyone else releases to all buyers
                releaseMode: finalData.sellerType === 'agent' ? finalData.releaseMode ?? 'all' : undefined,
                saleType: finalData.saleType || undefined,
                financingType: finalData.financingAvailable || undefined,
                showingInstruction: finalData.showingInstruction || undefined,
                physicalInspection: finalData.physicalInspection || undefined,
                occupancy: finalData.occupancy || undefined,
                marketStatus: finalData.marketStatus || undefined,
                rehabType: finalData.rehabType || undefined,
                rehabEstimate: finalData.rehabEstimate ? Number(finalData.rehabEstimate) : undefined,
                referralInviteId: referralInviteId || undefined,
            };

            const response = await listingsService.createListing(listingData);

            // Upload photos if any
            const photoUploadErrors: string[] = [];
            if (finalData.photos && Array.isArray(finalData.photos) && finalData.photos.length > 0) {
                const totalPhotos = finalData.photos.length;

                for (let i = 0; i < finalData.photos.length; i++) {
                    const photo = finalData.photos[i];
                    setUploadProgress(`Uploading photo ${i + 1} of ${totalPhotos}…`);

                    try {
                        // Get presigned URL
                        const uploadData = await listingsService.getPhotoUploadUrl(
                            response.id,
                            {
                                fileName: photo.name,
                                fileSize: photo.size,
                                mimeType: photo.type,
                            }
                        );

                        // Upload to S3
                        const s3Response = await fetch(uploadData.uploadUrl, {
                            method: 'PUT',
                            body: photo,
                            headers: {
                                'Content-Type': photo.type,
                            },
                        });

                        if (!s3Response.ok) {
                            throw new Error(`S3 upload failed with status ${s3Response.status}`);
                        }

                        // Confirm upload with full metadata
                        await listingsService.confirmPhotoUpload(response.id, {
                            s3Key: uploadData.key,
                            mimeType: photo.type,
                            fileSize: photo.size,
                        });
                    } catch (photoError: unknown) {
                        // Extract detailed error message from axios error
                        let errorMessage = 'Unknown error';
                        const axiosError = photoError as { response?: { data?: { message?: string } | string; status?: number }; message?: string };
                        if (axiosError?.response?.data && typeof axiosError.response.data === 'object' && 'message' in axiosError.response.data) {
                            errorMessage = axiosError.response.data.message || 'Unknown error';
                        } else if (axiosError?.response?.data) {
                            errorMessage = JSON.stringify(axiosError.response.data);
                        } else if (axiosError?.message) {
                            errorMessage = axiosError.message;
                        } else {
                            errorMessage = String(photoError);
                        }
                        console.error(`Failed to upload photo ${photo.name}:`, photoError);
                        photoUploadErrors.push(`${photo.name}: ${errorMessage}`);
                    }
                }
            }

            setUploadProgress("Finalizing…");
            // The listing exists at this point, so always move on: staying here and
            // re-submitting would create a duplicate listing. Photo failures are
            // reported on the success page with a link to add them from the editor.
            navigate('/sell/add/success', { state: { listing: response, photoUploadErrors } });
        } catch (error) {
            console.error("Error creating listing:", error);
            setSubmitError("We couldn't create your listing. Nothing was published — please try again.");
        } finally {
            setIsSubmitting(false);
            setUploadProgress("");
        }
    };

    // Get current step index based on path
    const currentStepIndex = (() => {
        const index = visibleSteps.findIndex((step) => location.pathname === step.path);
        return index >= 0 ? index : 0;
    })();
    const isLastStep = currentStepIndex === visibleSteps.length - 1;

    const getButtonText = () => {
        if (currentStepIndex === 0) return isPropertyLoaded ? 'Continue to condition' : 'Continue';
        if (currentStepIndex === 1) return 'Continue to access & photos';
        return 'Publish listing';
    };
    // Back walks the wizard, not browser history; step 1 has the "My listings" link instead
    const previousStepPath = currentStepIndex > 0 ? visibleSteps[currentStepIndex - 1]?.path : undefined;
    const showGate = isBuyerOnly && !isSuccessPage;

    const outletContext: AddPropertyOutletContext = {
        formData,
        updateFormData,
        submitForm,
        setFormId,
        isSubmitting,
        uploadProgress,
        setIsFormValid,
        isFormValid,
        setIsPropertyLoaded,
        isPropertyLoaded,
    };

    return (
        <div className="flex min-h-full flex-col">
            <PageHeader
                title={waitlisted ? "Saved to the waitlist" : isSuccessPage ? "Listing published" : "List a property"}
                description={
                    waitlisted
                        ? "We're not in your area yet."
                        : publishedToFirstLook
                        ? "A local investor makes you an instant cash offer before every buyer sees it."
                        : isSuccessPage
                        ? "Your property is live on the FlipIQ marketplace."
                        : showGate
                          ? "Turn on selling to list your first property."
                          : "Three short steps. Most details are filled in for you from public records."
                }
                backTo={isSuccessPage ? undefined : showGate ? "/buy" : "/sell"}
                backLabel={showGate ? "Marketplace" : "My listings"}
                size="md"
            >
                {!isBuyerOnly && <Stepper currentIndex={currentStepIndex} allComplete={isSuccessPage} />}
            </PageHeader>

            <PageContainer size="md" className="flex-1">
                {showGate ? (
                    <BecomeSellerCard isUnverified={!isTierLoading && tier <= 1} />
                ) : (
                    <Outlet context={outletContext} />
                )}

                {isLastStep && !isSuccessPage && !isBuyerOnly && !showGate && (
                    <div className="mt-6">
                        <PostUnderInviteSelect value={referralInviteId} onChange={setReferralInviteId} />
                    </div>
                )}

                {isLastStep && !isSuccessPage && !isBuyerOnly && firstLook?.eligible && (
                    <Alert
                        variant="info"
                        className="mt-6"
                        title={firstLook.named && firstLook.companyName ? `${firstLook.companyName} gets first look` : "A local investor gets first look"}
                        description={`${firstLook.named && firstLook.companyName ? `${firstLook.companyName}, who invited you,` : "One of our local experienced investors"} will call you and can send you an instant cash offer by ${firstLook.endsAtLabel}. If no offer comes in by then, your listing goes to every buyer automatically.`}
                    />
                )}

                {isLastStep && !isSuccessPage && !isBuyerOnly && !isTierLoading && tier <= 1 && (
                    <Alert
                        variant="warning"
                        title="Buyers will see that you're unverified"
                        description="Verified sellers get more bids. You can publish now and verify later."
                        action={
                            <Button variant="link" size="sm" onClick={() => setIsTierModalOpen(true)}>
                                See how to get verified
                            </Button>
                        }
                        className="mt-6"
                    />
                )}

                {submitError && (
                    <Alert
                        variant="error"
                        title="Listing not created"
                        description={submitError}
                        dismissible
                        onDismiss={() => setSubmitError(null)}
                        className="mt-6"
                    />
                )}
            </PageContainer>

            {/* Sticky wizard footer */}
            {showActionBtns && (
                <div className="sticky bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85">
                    <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
                        {previousStepPath ? (
                            <Button
                                variant="secondary"
                                onClick={() => navigate(previousStepPath)}
                                disabled={isSubmitting}
                            >
                                Back
                            </Button>
                        ) : (
                            // Keeps Continue on the right when there is no previous step
                            <span aria-hidden />
                        )}

                        <p className="hidden min-w-0 truncate text-meta text-gray-500 sm:block" aria-live="polite">
                            {uploadProgress || (
                                <>
                                    Step <span className="tabular font-medium text-gray-900">{currentStepIndex + 1}</span> of{" "}
                                    <span className="tabular">{visibleSteps.length}</span>
                                </>
                            )}
                        </p>

                        {/* Always enabled: submitting validates and points at the first missing field */}
                        <Button
                            variant="brand"
                            type="submit"
                            form={formId}
                            loading={isSubmitting}
                            disabled={isSubmitting}
                            rightIcon={isLastStep ? <Send /> : undefined}
                        >
                            {isSubmitting ? (
                                "Publishing…"
                            ) : (
                                <>
                                    <span className="sm:hidden">{isLastStep ? "Publish" : "Continue"}</span>
                                    <span className="hidden sm:inline">{getButtonText()}</span>
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            <TierBenefitsModal
                isOpen={isTierModalOpen}
                onClose={() => setIsTierModalOpen(false)}
                currentTier={(Math.min(Math.max(tier, 1), 4)) as 1 | 2 | 3 | 4}
            />
        </div>
    );
};

export default AddPropertyLayout;
